import { MockDirectorService, ScriptEntry } from './MockDirectorService';
import { DirectorResponse } from '@/types/director.types';
import { TimelineEntry } from '@/types/demo.types';

/**
 * Demo Director Service
 * Wrapper around MockDirectorService that provides the same interface as WebSocketDirectorService
 * but uses polling instead of WebSocket connections
 */
export class DemoDirectorService {
  private mockService: MockDirectorService;
  private pollInterval: number | null = null;
  private onUpdateCallback: ((view: DirectorResponse) => void) | null = null;

  /**
   * Create a new DemoDirectorService
   * @param timeline - Timeline entries from demo config
   */
  constructor(timeline: TimelineEntry[]) {
    // Convert timeline format to script format for MockDirectorService
    const script: ScriptEntry[] = timeline.map(entry => ({
      time: entry.time,
      cameraPresetKey: entry.cameraPresetKey,
      shotIds: entry.shots.map(s => s.id),
    }));

    // Flatten all shots from timeline
    const shots = timeline.flatMap(e => e.shots);

    // Initialize mock service with converted data
    this.mockService = new MockDirectorService(script, shots);

    console.log('[DemoDirectorService] Initialized with timeline:', {
      entries: timeline.length,
      totalShots: shots.length,
    });
  }

  /**
   * Start polling the mock service for updates
   * @param onUpdate - Callback for director updates
   */
  connect(onUpdate: (view: DirectorResponse) => void): void {
    this.onUpdateCallback = onUpdate;

    // Poll mock service every 500ms
    this.pollInterval = window.setInterval(() => {
      const view = this.mockService.getCurrentView();
      if (this.onUpdateCallback) {
        this.onUpdateCallback(view);
      }
    }, 500);

    console.log('[DemoDirectorService] Started polling');
  }

  /**
   * Stop polling
   */
  disconnect(): void {
    if (this.pollInterval) {
      clearInterval(this.pollInterval);
      this.pollInterval = null;
    }
    this.onUpdateCallback = null;
    console.log('[DemoDirectorService] Stopped polling');
  }

  /**
   * Reset the timeline to beginning
   */
  reset(): void {
    this.mockService.reset();
    console.log('[DemoDirectorService] Timeline reset');
  }

  /**
   * Get current view without waiting for next poll
   */
  getCurrentView(): DirectorResponse {
    return this.mockService.getCurrentView();
  }

  /**
   * Check if service is connected (polling)
   */
  isConnected(): boolean {
    return this.pollInterval !== null;
  }
}
